import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSION, can, shopApi, useAuth } from '@wa/shared';
import type { CustomerGroup, PriceRow, PriceRowInput, ProductDetail, ShopProductInput, VariantInput } from '@wa/shared';
import { Card, Field, Notice, Switch, danger, errorText, input, primary, secondary } from './ui';

interface VariantDraft { id: number | null; name: string; sku: string; attributes: string; listPrice: string; stock: string }

const emptyVariant = (): VariantDraft => ({ id: null, name: 'Default', sku: '', attributes: '', listPrice: '', stock: '' });

/**
 * One product: details, variants, pictures, visibility — and, below, its price
 * grid per customer type (a separate save, a separate permission).
 */
export function ProductEditor({ productId, onClose }: { productId: number | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const detail = useQuery({ queryKey: ['shop', 'product', productId], queryFn: () => shopApi.product(productId!), enabled: productId !== null });
  const categories = useQuery({ queryKey: ['shop', 'categories'], queryFn: shopApi.categories });
  const groups = useQuery({ queryKey: ['shop', 'groups'], queryFn: shopApi.groups });

  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [newCategory, setNewCategory] = useState('');
  const [sku, setSku] = useState('');
  const [hsn, setHsn] = useState('');
  const [taxRate, setTaxRate] = useState('18');
  const [listPrice, setListPrice] = useState('');
  const [unit, setUnit] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [visible, setVisible] = useState<number[] | null>(null);
  const [variants, setVariants] = useState<VariantDraft[]>([emptyVariant()]);
  const [images, setImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    setName(d.product.name); setDescription(d.product.description ?? ''); setCategoryId(d.product.categoryId);
    setSku(d.product.sku ?? ''); setHsn(d.product.hsn ?? ''); setTaxRate(String(d.product.taxRate)); setListPrice(String(d.product.listPrice));
    setUnit(d.product.unit ?? ''); setIsActive(d.product.isActive);
    setVisible(d.product.visibleGroups ? (JSON.parse(d.product.visibleGroups) as number[]) : null);
    setVariants(d.variants.filter((v) => v.isActive).map((v) => ({ id: v.id, name: v.name, sku: v.sku ?? '', attributes: v.attributes ?? '', listPrice: v.listPrice === null ? '' : String(v.listPrice), stock: v.stock === null ? '' : String(v.stock) })));
    setImages(d.images.map((i) => i.url));
  }, [detail.data]);

  const save = useMutation({
    mutationFn: (input: ShopProductInput) => (productId === null ? shopApi.createProduct(input) : shopApi.updateProduct(productId, input)),
    onSuccess: (saved: ProductDetail) => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['shop'] });
      if (productId === null) onClose();
      else queryClient.setQueryData(['shop', 'product', productId], saved);
    },
    onError: (e) => setError(errorText(e, 'Could not save the product.')),
  });
  const remove = useMutation({
    mutationFn: () => shopApi.deleteProduct(productId!),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['shop'] }); onClose(); },
    onError: (e) => setError(errorText(e, 'Could not hide the product.')),
  });

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true); setError(null);
    try {
      for (const file of Array.from(files).slice(0, 8 - images.length)) {
        const { url } = await shopApi.uploadImage(file);
        setImages((imgs) => [...imgs, url]);
      }
    } catch (e) { setError(errorText(e, 'Upload failed.')); }
    finally { setUploading(false); }
  };

  const body = (): ShopProductInput => ({
    name: name.trim(),
    description: description || null,
    categoryId: newCategory ? null : categoryId ?? 0,
    categoryName: newCategory || null,
    sku: sku || null, hsn: hsn || null,
    taxRate: Number(taxRate) || 0,
    listPrice: Number(listPrice) || 0,
    unit: unit || null,
    isActive,
    visibleGroups: visible ?? [],
    variants: variants.map<VariantInput>((v, i) => ({
      id: v.id, name: v.name.trim() || 'Default', sku: v.sku || null, attributes: v.attributes || null,
      listPrice: v.listPrice === '' ? null : Number(v.listPrice), stock: v.stock === '' ? null : Number(v.stock), isActive: true, sortOrder: i,
    })),
    images,
  });

  const canSave = name.trim().length > 0 && listPrice !== '' && !save.isPending;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <button type="button" className={secondary} onClick={onClose}>← Products</button>
        <h2 className="text-body font-semibold">{productId === null ? 'New product' : detail.data?.product.name ?? '…'}</h2>
      </div>
      {error && <Notice kind="bad">{error}</Notice>}

      <Card title="Details">
        <div className="grid gap-3 @min-[32rem]:grid-cols-2">
          <Field label="Name" className="@min-[32rem]:col-span-2"><input className={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} /></Field>
          <Field label="Description" className="@min-[32rem]:col-span-2"><textarea className={`${input} min-h-20`} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
          <Field label="Category">
            <select className={input} value={newCategory ? 'new' : categoryId ?? ''} onChange={(e) => { if (e.target.value === 'new') { setNewCategory(' '); setCategoryId(null); } else { setNewCategory(''); setCategoryId(e.target.value ? Number(e.target.value) : null); } }}>
              <option value="">None</option>
              {categories.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value="new">New category…</option>
            </select>
          </Field>
          {newCategory && <Field label="New category name"><input className={input} value={newCategory.trim()} onChange={(e) => setNewCategory(e.target.value || ' ')} maxLength={80} /></Field>}
          <Field label="List price (₹)" hint="The default price; customer types can override it below."><input className={input} type="number" min={0} step="0.01" value={listPrice} onChange={(e) => setListPrice(e.target.value)} /></Field>
          <Field label="GST %"><input className={input} type="number" min={0} max={100} step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} /></Field>
          <Field label="SKU"><input className={input} value={sku} onChange={(e) => setSku(e.target.value)} maxLength={60} /></Field>
          <Field label="HSN"><input className={input} value={hsn} onChange={(e) => setHsn(e.target.value)} maxLength={12} /></Field>
          <Field label="Unit" hint="piece, kg, box…"><input className={input} value={unit} onChange={(e) => setUnit(e.target.value)} maxLength={20} /></Field>
        </div>
        <Switch checked={isActive} onChange={setIsActive} label="Shown on the page" />
      </Card>

      <Card title="Pictures" blurb="Up to eight. The first is the one in the list.">
        <div className="flex flex-wrap gap-2">
          {images.map((url, i) => (
            <div key={url} className="relative">
              <img src={url} alt="" className="size-20 rounded object-cover" />
              <div className="mt-1 flex gap-1">
                {i > 0 && <button type="button" className="text-micro underline" onClick={() => setImages((im) => { const c = [...im]; const prev = c[i - 1]!; c[i - 1] = c[i]!; c[i] = prev; return c; })}>←</button>}
                <button type="button" className="text-micro text-(--color-bad) underline" onClick={() => setImages((im) => im.filter((u) => u !== url))}>remove</button>
              </div>
            </div>
          ))}
          {images.length < 8 && (
            <label className="flex size-20 cursor-pointer items-center justify-center rounded border border-dashed border-(--color-line) text-micro text-(--color-muted)">
              {uploading ? '…' : '+ add'}
              <input type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" disabled={uploading} onChange={(e) => void upload(e.target.files)} />
            </label>
          )}
        </div>
      </Card>

      <Card title="Variants" blurb="Size, colour, pack — each a separate line in the cart with its own SKU and stock. Leave stock empty when you do not track it."
            actions={<button type="button" className={secondary} onClick={() => setVariants((v) => [...v, { ...emptyVariant(), name: '' }])}>Add variant</button>}>
        <div className="flex flex-col gap-2">
          {variants.map((v, i) => (
            <div key={v.id ?? `new-${i}`} className="grid gap-2 rounded border border-(--color-line-dim) p-2 @min-[40rem]:grid-cols-[1fr_8rem_1fr_7rem_6rem_auto]">
              <input className={input} placeholder="Name (Chrome / 600 mm)" value={v.name} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <input className={input} placeholder="SKU" value={v.sku} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, sku: e.target.value } : x)))} />
              <input className={input} placeholder='{"finish":"Chrome"}' value={v.attributes} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, attributes: e.target.value } : x)))} />
              <input className={input} type="number" placeholder="Price" title="Overrides the list price" value={v.listPrice} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, listPrice: e.target.value } : x)))} />
              <input className={input} type="number" placeholder="Stock" value={v.stock} onChange={(e) => setVariants((vs) => vs.map((x, j) => (j === i ? { ...x, stock: e.target.value } : x)))} />
              <button type="button" className={danger} disabled={variants.length === 1} onClick={() => setVariants((vs) => vs.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Who sees it" blurb="Everyone, or only some customer types — dealer-only ranges, for example.">
        <Switch checked={visible === null} onChange={(all) => setVisible(all ? null : [])} label="Every customer type" />
        {visible !== null && (
          <div className="flex flex-wrap gap-3">
            {groups.data?.map((g: CustomerGroup) => (
              <label key={g.id} className="flex items-center gap-1.5 text-small">
                <input type="checkbox" className="accent-(--color-brand)" checked={visible.includes(g.id)}
                       onChange={(e) => setVisible((v) => (e.target.checked ? [...(v ?? []), g.id] : (v ?? []).filter((x) => x !== g.id)))} />
                {g.name}
              </label>
            ))}
          </div>
        )}
      </Card>

      <div className="flex flex-wrap gap-2">
        <button type="button" className={primary} disabled={!canSave} onClick={() => save.mutate(body())}>{save.isPending ? 'Saving…' : 'Save product'}</button>
        {productId !== null && <button type="button" className={danger} disabled={remove.isPending} onClick={() => { if (window.confirm('Hide this product from the page? Orders that name it keep it.')) remove.mutate(); }}>Hide product</button>}
      </div>

      {productId !== null && detail.data && groups.data && (
        <PriceGrid product={detail.data} groups={groups.data} editable={can(user, PERMISSION.shopPrices)} />
      )}
    </div>
  );
}

/**
 * Rows = variants, columns = customer types; an empty cell means "list price".
 * Quantity breaks are the "+ break" rows under a cell. Saved as one call.
 */
function PriceGrid({ product, groups, editable }: { product: ProductDetail; groups: CustomerGroup[]; editable: boolean }) {
  const queryClient = useQueryClient();
  const prices = useQuery({ queryKey: ['shop', 'prices', product.product.id], queryFn: () => shopApi.prices(product.product.id) });
  const [rows, setRows] = useState<PriceRowInput[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (prices.data) setRows(prices.data.map((r: PriceRow) => ({ variantId: r.variantId, groupId: r.groupId, minQty: r.minQty, price: r.price })));
  }, [prices.data]);

  const save = useMutation({
    mutationFn: (r: PriceRowInput[]) => shopApi.savePrices(product.product.id, r),
    onSuccess: () => { setError(null); void queryClient.invalidateQueries({ queryKey: ['shop', 'prices', product.product.id] }); },
    onError: (e) => setError(errorText(e, 'Could not save prices.')),
  });

  if (!rows) return null;
  const variants = product.variants.filter((v) => v.isActive);
  const cell = (variantId: number, groupId: number, minQty: number) => rows.find((r) => r.variantId === variantId && r.groupId === groupId && (r.minQty ?? 1) === minQty);
  const setCell = (variantId: number, groupId: number, minQty: number, value: string) => setRows((rs) => {
    const rest = (rs ?? []).filter((r) => !(r.variantId === variantId && r.groupId === groupId && (r.minQty ?? 1) === minQty));
    return value === '' ? rest : [...rest, { variantId, groupId, minQty, price: Number(value) }];
  });
  const breaksFor = (variantId: number, groupId: number) => rows.filter((r) => r.variantId === variantId && r.groupId === groupId && (r.minQty ?? 1) > 1).map((r) => r.minQty ?? 1).sort((a, b) => a - b);

  return (
    <Card title="Prices by customer type" blurb={`Empty means the list price (₹${product.product.listPrice.toLocaleString('en-IN')} or the variant's own). Add a quantity break to price 10+ or 50+ lower.`}>
      {error && <Notice kind="bad">{error}</Notice>}
      <div className="overflow-x-auto">
        <table className="w-full text-small">
          <thead>
            <tr className="text-left text-micro uppercase tracking-wide text-(--color-faint)">
              <th className="py-1 pr-3">Variant</th>
              {groups.map((g) => <th key={g.id} className="py-1 pr-3">{g.name}</th>)}
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => (
              <tr key={v.id} className="border-t border-(--color-line-dim) align-top">
                <td className="py-2 pr-3">
                  <div className="font-medium">{v.name}</div>
                  <div className="text-micro text-(--color-muted)">list {rupeesShort(v.listPrice ?? product.product.listPrice)}</div>
                </td>
                {groups.map((g) => (
                  <td key={g.id} className="py-2 pr-3">
                    <input className={`${input} w-28`} type="number" min={0} step="0.01" disabled={!editable} placeholder="list"
                           value={cell(v.id, g.id, 1)?.price ?? ''} onChange={(e) => setCell(v.id, g.id, 1, e.target.value)} />
                    {breaksFor(v.id, g.id).map((q) => (
                      <div key={q} className="mt-1 flex items-center gap-1 text-micro text-(--color-muted)">
                        <span className="w-8">{q}+</span>
                        <input className={`${input} w-24`} type="number" min={0} step="0.01" disabled={!editable}
                               value={cell(v.id, g.id, q)?.price ?? ''} onChange={(e) => setCell(v.id, g.id, q, e.target.value)} />
                      </div>
                    ))}
                    {editable && (
                      <button type="button" className="mt-1 text-micro text-(--color-muted) underline"
                              onClick={() => { const q = Number(window.prompt('From what quantity?', '10')); if (q > 1) setCell(v.id, g.id, q, String(cell(v.id, g.id, 1)?.price ?? v.listPrice ?? product.product.listPrice)); }}>
                        + break
                      </button>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable
        ? <div><button type="button" className={primary} disabled={save.isPending} onClick={() => save.mutate(rows)}>{save.isPending ? 'Saving…' : 'Save prices'}</button></div>
        : <Notice kind="muted">You can see prices but not change them — that needs the "Set prices" permission.</Notice>}
    </Card>
  );
}

const rupeesShort = (v: number) => '₹' + v.toLocaleString('en-IN');
